import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button,
    alpha
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { COLORS, jsonToXlsx } from '../../../settings/themeSettings';

const BalanceAgeeViewer = ({ data, onSelectClient }) => {
    const [activeRowIdx, setActiveRowIdx] = useState(0);
    const tableRef = useRef(null);

    if (!data) return null;

    const {
        dateSituationStr = 'Ce jour',
        rows = [],
        totals = {
            soldeDu: 0,
            nonEchu: 0,
            tranche0_30: 0,
            tranche31_60: 0,
            tranche61_90: 0,
            tranchePlus90: 0
        }
    } = data;

    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.min(rows.length - 1, prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.max(0, prev - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const selected = rows[activeRowIdx];
            if (selected && onSelectClient) {
                onSelectClient(selected);
            }
        }
    };

    const handleExportExcel = () => {
        const exportData = rows.map(r => ({
            'Client': r.nom,
            'Solde dû (CFA)': r.soldeDu,
            'Non échu (CFA)': r.nonEchu,
            'De 0 à 30 j (CFA)': r.tranche0_30,
            'De 31 à 60 j (CFA)': r.tranche31_60,
            'De 61 à 90 j (CFA)': r.tranche61_90,
            'Plus de 90 j (CFA)': r.tranchePlus90
        }));
        jsonToXlsx(exportData, `Balance_Agee_Clients_${dateSituationStr.replace(/\//g, '-')}`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box tabIndex={0} ref={tableRef} onKeyDown={handleKeyDown} sx={{ outline: 'none' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {rows.length} client{rows.length > 1 ? 's' : ''} avec solde dû à la date du {dateSituationStr}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1100px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL COMMUN */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#0f172a' }}>
                                Balance âgée clients
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block">
                                <strong>À la date du :</strong> {dateSituationStr}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* TABLEAU BALANCE ÂGÉE (PAGES 19 & 20) */}
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.8fr 140px 120px 120px 120px 120px 130px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
                        <Box>Client</Box>
                        <Box sx={{ textAlign: 'right' }}>Solde dû</Box>
                        <Box sx={{ textAlign: 'right' }}>Non échu</Box>
                        <Box sx={{ textAlign: 'right' }}>De 0 à 30 j</Box>
                        <Box sx={{ textAlign: 'right' }}>De 31 à 60 j</Box>
                        <Box sx={{ textAlign: 'right' }}>De 61 à 90 j</Box>
                        <Box sx={{ textAlign: 'right' }}>Plus de 90 j</Box>
                    </Box>

                    <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        {rows.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                Aucun encours client non soldé à la date du {dateSituationStr}.
                            </Box>
                        ) : (
                            rows.map((r, idx) => {
                                const isSelected = activeRowIdx === idx;
                                return (
                                    <Box
                                        key={r.id || idx}
                                        onClick={() => {
                                            setActiveRowIdx(idx);
                                            if (onSelectClient) onSelectClient(r);
                                        }}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1.8fr 140px 120px 120px 120px 120px 130px',
                                            p: 1.1,
                                            alignItems: 'center',
                                            fontSize: '0.85rem',
                                            borderBottom: '1px solid #e2e8f0',
                                            bgcolor: isSelected ? alpha(COLORS.primary, 0.12) : (idx % 2 === 0 ? 'white' : '#f8fafc'),
                                            borderLeft: isSelected ? `4px solid ${COLORS.primary}` : '4px solid transparent',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: alpha(COLORS.primary, 0.08)
                                            }
                                        }}
                                    >
                                        <Box sx={{ fontWeight: 700, color: '#0f172a' }}>{r.nom}</Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                                            {formatCurrency(r.soldeDu)}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', color: r.nonEchu > 0 ? '#16a34a' : 'inherit' }}>
                                            {r.nonEchu > 0 ? formatCurrency(r.nonEchu) : '0'}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', color: r.tranche0_30 > 0 ? '#ea580c' : 'inherit' }}>
                                            {r.tranche0_30 > 0 ? formatCurrency(r.tranche0_30) : '0'}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', color: r.tranche31_60 > 0 ? '#ea580c' : 'inherit' }}>
                                            {r.tranche31_60 > 0 ? formatCurrency(r.tranche31_60) : '0'}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', color: r.tranche61_90 > 0 ? '#dc2626' : 'inherit', fontWeight: r.tranche61_90 > 0 ? 700 : 400 }}>
                                            {r.tranche61_90 > 0 ? formatCurrency(r.tranche61_90) : '0'}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', color: r.tranchePlus90 > 0 ? '#dc2626' : 'inherit', fontWeight: r.tranchePlus90 > 0 ? 800 : 400 }}>
                                            {r.tranchePlus90 > 0 ? formatCurrency(r.tranchePlus90) : '0'}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* LIGNE DES TOTAUX (PAGE 19) */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1.8fr 140px 120px 120px 120px 120px 130px',
                            bgcolor: '#f1f5f9',
                            p: 1.2,
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ fontWeight: 800, color: '#0f172a' }}>
                            TOTAUX :
                        </Box>
                        <Box sx={{ textAlign: 'right', color: totals.soldeDu > 0 ? '#dc2626' : '#0f172a', fontWeight: 900 }}>
                            {formatCurrency(totals.soldeDu)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#16a34a' }}>
                            {formatCurrency(totals.nonEchu)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#ea580c' }}>
                            {formatCurrency(totals.tranche0_30)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#ea580c' }}>
                            {formatCurrency(totals.tranche31_60)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#dc2626' }}>
                            {formatCurrency(totals.tranche61_90)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#dc2626', fontWeight: 800 }}>
                            {formatCurrency(totals.tranchePlus90)}
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default BalanceAgeeViewer;
